import React, { useCallback, useState } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
  Control,
} from "react-hook-form";
import ToggleButton from "../../../components/ToggleButton";
import FormErrorMessage from "../../../components/common/FormErrorMessage";
import { OrderFormData, LocationState } from "../../../types/types";
import {
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
  useDistrictFetchData,
} from "../../../apis/locationApis";
import { IconUser } from "@tabler/icons-react";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import TextareaField from "../../../components/common/TextareaField";
import SelectInput from "../../../components/Select";
import Input from "../../../components/Input";

interface SenderSectionProps {
  clients: any[];
  register: UseFormRegister<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  clearErrors: UseFormClearErrors<OrderFormData>;
  watch: any;
  useDifferentSender: boolean;
  setUseDifferentSender: (value: boolean) => void;
  useFMOrder?: boolean;
  setUseFMOrder?: (value: boolean) => void;
  onClientSelect: (clientId: string) => void;
  control: Control<OrderFormData>;
  isSidebar?: boolean;
}

// ── Staff-style section header ────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, right }: { icon?: any; title: string; right?: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-2 mb-2">
    <p className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">{title}</p>
    {right}
  </div>
);

const SenderSection: React.FC<SenderSectionProps> = ({
  clients,
  register,
  errors,
  setValue,
  clearErrors,
  watch,
  useDifferentSender,
  setUseDifferentSender,
  useFMOrder: _useFMOrder,
  setUseFMOrder: _setUseFMOrder,
  onClientSelect,
  control,
  isSidebar = false,
}) => {
  const [senderLocation, setSenderLocation] = React.useState<LocationState>({});
  const [searchRef, setSearchRef] = useState<string>("");
  const [districtSearchRef, setDistrictSearchRef] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<any>("manual");

  const { data: countries = [] } = useFetchCountriesAllData();
  const { data: senderCities = [], refetch: fetchSenderCities, isPending: isCitiesFetchPending } =
    useFetchCountryCitiesAllData(senderLocation?.country?.id, searchRef, selectedOption);
  const { data: senderDistricts = [], refetch: fetchSenderDistricts, isPending: isDistrictFetchPending } =
    useDistrictFetchData(senderLocation?.city?.id, districtSearchRef, selectedOption);

  const handleLocationChange = (field: keyof LocationState, value: any) => {
    if (!value?.id) return;
    setSenderLocation((prev) => {
      const newLoc = { ...prev, [field]: value };
      if (field === "country") { newLoc.city = undefined; newLoc.district = undefined; setValue("sender.city", ""); setValue("sender.district", ""); }
      else if (field === "city") { newLoc.district = undefined; setValue("sender.district", ""); }
      return newLoc;
    });
    setValue(`sender.${field}` as any, value.id);
    setValue(`sender.${field}Name` as any, value.name);
    setValue("sender.dialCode", value?.dialCode);
    if (field === "country" && value.id) fetchSenderCities();
    else if (field === "city" && value.id) fetchSenderDistricts();
  };

  const handleCitySearch = (query: string) => {
    if (query?.trim().length >= 3) { setSearchRef(query); fetchSenderCities(); }
  };

  const handleCityChange = useCallback((val: string) => {
    const city = senderCities?.find((c: any) => c.id === val);
    if (!city) return;
    setValue("sender.city", city.id); setValue("sender.cityName", city.name);
    setSenderLocation((prev) => ({ ...prev, city, district: undefined }));
    setValue("sender.district", "");
    fetchSenderDistricts();
  }, [senderCities, setValue, fetchSenderDistricts]);

  const handleDistrictSearch = (query: string) => {
    if (query.trim().length >= 3) { setDistrictSearchRef(query); fetchSenderDistricts(); }
  };

  const handleDistrictChange = useCallback((val: string) => {
    const d = senderDistricts.find((x: any) => x.id === val);
    if (!d) return;
    setValue("sender.district", d.id); setValue("sender.districtName", d.name);
    setSenderLocation((prev) => ({ ...prev, district: d }));
  }, [senderDistricts, setValue]);

  if (isSidebar) {
    return (
      <div className="space-y-4">
        <SectionHeader
          icon={IconUser}
          title="Sender Details"
          right={
            <div className="relative z-10 flex items-center gap-1.5">
              <ToggleButton onChange={() => setUseDifferentSender(!useDifferentSender)} checked={useDifferentSender} />
              <span className="text-xs text-slate-500">Different address</span>
            </div>
          }
        />

        {!useDifferentSender && (
          <NewSelectInput
            name="clientId"
            label="Client"
            placeholder="Select client"
            className="flex items-center justify-between"
            labelClass="w-1/3"
            selectOptions={clients?.map((c) => ({ id: c?.id, value: c?.id, text: c?.name })) || []}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: "Client is required" }}
            error={errors.clientId}
            disabled={!clients?.length}
            value={watch("clientId")}
            setValue={onClientSelect}
          />
        )}

        {useDifferentSender && (
          <>
            <NewSelectInput name="sender.country" label="Country" placeholder="Select country"
              className="flex items-center justify-between" labelClass="w-1/3"
              selectOptions={countries.map((c: any) => ({ id: c.id, value: c.id, text: c.name }))}
              register={register} clearErrors={clearErrors} validation={{ required: "Required" }}
              error={errors.sender?.country} disabled={!countries.length}
              value={senderLocation.country?.id}
              setValue={(val: string) => { const c = countries.find((x: any) => x.id === val); handleLocationChange("country", c); }} />

            <NewinputField name="sender.name" type="text" title="Name" register={register}
              error={errors.sender?.name} validation={{ required: "Required" }}
              className="flex items-center justify-between" labelClass="w-1/3" />

            <div className="flex items-center justify-between">
              <label className="text-[#1E293B] text-sm w-1/3 whitespace-nowrap">
                Mobile<span className="text-red-600">*</span>
              </label>
              <div className="w-full">
                <div className={`flex items-center rounded-xl border bg-white px-3 py-[6px] ${errors.sender?.mobile ? "border-red-400" : "border-[#D8D8D8]"}`}>
                  <span className="text-gray-500 text-sm whitespace-nowrap border-r border-gray-200 pr-2 mr-2">
                    {watch("sender.dialCode") ? `+${watch("sender.dialCode")}` : "+--"}
                  </span>
                  <input type="number" placeholder="Mobile number"
                    className="w-full outline-none bg-transparent text-sm text-[#1E1E1E] hide-spinner"
                    {...register("sender.mobile", {
                      required: "Required",
                      validate: (v: string) => { const t = v?.toString().trim(); return (t.length === 9 || t.length === 10) || "9-10 digits"; }
                    })} />
                </div>
                {errors.sender?.mobile && <FormErrorMessage error={errors.sender?.mobile} />}
              </div>
            </div>

            <NewinputField name="sender.email" type="text" title="Email" register={register}
              error={errors.sender?.email}
              validation={{ required: "Required", setValueAs: (v: string) => v?.toLowerCase(), pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email" } }}
              className="flex items-center justify-between" labelClass="w-1/3" />

            <NewSelectInput name="sender.city" label="City" placeholder="Select city"
              className="flex items-center justify-between" labelClass="w-1/3"
              selectOptions={senderCities.map((c: any) => ({ id: c.id, value: c.id, text: c.name }))}
              clearErrors={clearErrors} register={register} validation={{ required: "Required" }}
              error={errors.sender?.city} value={senderLocation.city?.id} setValue={handleCityChange} />

            <NewSelectInput name="sender.district" label="District" placeholder="Select district"
              className="flex items-center justify-between" labelClass="w-1/3"
              selectOptions={senderDistricts.map((d: any) => ({ id: d.id, value: d.id, text: d.name }))}
              clearErrors={clearErrors} register={register} validation={{ required: "Required" }}
              error={errors.sender?.district} value={senderLocation.district?.id} setValue={handleDistrictChange} />

            <NewinputField name="sender.zipCode" type="number" title="Zip Code" register={register}
              error={errors.sender?.zipCode} validation={{ required: "Required" }}
              className="flex items-center justify-between" labelClass="w-1/3" />

            <NewinputField name="sender.latitude" type="number" title="Latitude" register={register}
              error={errors.sender?.latitude}
              className="flex items-center justify-between" labelClass="w-1/3" />

            <NewinputField name="sender.longitude" type="number" title="Longitude" register={register}
              error={errors.sender?.longitude}
              className="flex items-center justify-between" labelClass="w-1/3" />

            <div className="flex items-start justify-between">
              <label className="text-[#1E293B] text-sm w-1/3 pt-1 whitespace-nowrap shrink-0">
                Address<span className="text-red-600">*</span>
              </label>
              <div className="flex-1">
                <TextareaField rows={2} label="" register={register} name="sender.address" placeholder=""
                  error={errors.sender?.address} validation={{ required: "Required" }} />
              </div>
            </div>

            <div className="flex items-start justify-between">
              <label className="text-[#1E293B] text-sm w-1/3 pt-1 whitespace-nowrap shrink-0">
                Natl. Address<span className="text-red-600">*</span>
              </label>
              <div className="flex-1">
                <TextareaField rows={2} label="" register={register} placeholder="" name="sender.nationalAddress"
                  error={errors.sender?.nationalAddress} validation={{ required: "Required" }} />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Non-sidebar original layout
  return (
    <div className="col-span-1">
      <div className="col-span-6 flex items-center space-x-4 justify-between">
        <p className="text-black font-medium text-base uppercase">Sender Details</p>
        <div className="flex justify-start items-center gap-1">
          <ToggleButton onChange={() => setUseDifferentSender(!useDifferentSender)} checked={useDifferentSender} />
          <p className="text-[#1E293B] text-sm">Send from different address</p>
        </div>
      </div>
      {!useDifferentSender && (
        <SelectInput className="col-span-2" name="clientId" label="Select Client" placeholder="Name"
          selectOptions={clients?.map((c) => ({ id: c?.id, value: c?.id, text: c?.name })) || []}
          clearErrors={clearErrors} register={register} validation={{ required: "Client is required" }}
          error={errors.clientId} disabled={!clients?.length} value={watch("clientId")} setValue={onClientSelect} />
      )}
      {useDifferentSender && (
        <div className="mt-4 grid grid-cols-6 gap-2">
          <SelectInput name="sender.country" className="col-span-6" label="Country" placeholder="Select Country"
            selectOptions={countries.map((c: any) => ({ id: c.id, value: c.id, text: c.name }))}
            register={register} clearErrors={clearErrors} validation={{ required: "Sender country is required" }}
            error={errors.sender?.country} disabled={!countries.length} value={senderLocation.country?.id}
            setValue={(val: string) => { const c = countries.find((x: any) => x.id === val); handleLocationChange("country", c); }} />
          <Input name="sender.name" type="text" title="Name" register={register} error={errors.sender?.name} validation={{ required: "Sender name is required" }} className="col-span-2" />
          <Input name="sender.email" type="text" title="Email" register={register} error={errors.sender?.email}
            validation={{ required: "Sender email is required", setValueAs: (v: string) => v?.toLowerCase(), pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email" } }} className="col-span-2" />
          <SelectInput name="sender.city" label="City" className="col-span-2" placeholder="Select City"
            selectOptions={senderCities.map((c: any) => ({ id: c.id, value: c.id, text: c.name }))}
            clearErrors={clearErrors} register={register} validation={{ required: "Sender city is required" }}
            error={errors.sender?.city} value={senderLocation.city?.id}
            setValue={handleCityChange} onSearch={handleCitySearch} isPending={isCitiesFetchPending} />
          <SelectInput name="sender.district" label="District" className="col-span-2" placeholder="Select District"
            selectOptions={senderDistricts.map((d: any) => ({ id: d.id, value: d.id, text: d.name }))}
            clearErrors={clearErrors} register={register} validation={{ required: "Sender district is required" }}
            error={errors.sender?.district} value={senderLocation.district?.id}
            setValue={handleDistrictChange} onSearch={handleDistrictSearch} isPending={isDistrictFetchPending} />
          <Input name="sender.zipCode" type="number" title="Zip Code" register={register} error={errors.sender?.zipCode} validation={{ required: "Sender zipcode is required" }} className="col-span-2" />
          <Input name="sender.latitude" type="number" title="Latitude" register={register} error={errors.sender?.latitude} className="col-span-2" />
          <Input name="sender.longitude" type="number" title="Longitude" register={register} error={errors.sender?.longitude} className="col-span-2" />
          <TextareaField rows={2} className="col-span-3" label="Address" register={register} name="sender.address" placeholder="" error={errors.sender?.address} validation={{ required: "Sender address is required" }} />
          <TextareaField rows={2} className="col-span-3" label="National Address" register={register} placeholder="" name="sender.nationalAddress" error={errors.sender?.nationalAddress} validation={{ required: "Sender national address is required" }} />
        </div>
      )}
    </div>
  );
};

export default SenderSection;